import { ApolloClient, ApolloLink, Observable } from '@apollo/client'

import type { Token } from '@web-app/lib/ironSession'

import { buildCache } from './cache'
import { isAuthSignatureError } from './links'
import { createApolloSession } from './session'
import type { SessionRefs } from './session'
import { tokenIsExpiring } from './tokenMachine'
import type { MintResult } from './tokenMachine'

export { isAuthSignatureError, tokenIsExpiring }
export type { MintResult }

// The one module-level binding: something must outlive React renders and be
// reachable from _app. Everything mutable lives inside it.
let browserSession: ReturnType<typeof createApolloSession> | null = null
const getBrowserSession = () => (browserSession ??= createApolloSession())

// Server rendering never queries the backend: the browser fetches once it has
// hydrated, and the API routes have createServerApolloClient. A render still
// needs a client for ApolloProvider to hold, so this one exists to be held.
// An operation that reaches it is a bug, and fails here instead of leaving a
// Vercel function anonymously.
const createRenderOnlyClient = () =>
  new ApolloClient({
    ssrMode: true,
    link: new ApolloLink(
      () =>
        new Observable((observer) => {
          observer.error(
            new Error(
              'GraphQL during server rendering: fetch from the browser, or from an API route through createServerApolloClient.',
            ),
          )
        }),
    ),
    cache: buildCache(),
  })

// The browser's door. Props carry identity only; userId is the key: a change
// swaps client, cache and credential together, never on token refresh.
export const initializeApollo = (session: SessionRefs = {}) => {
  if (typeof window === 'undefined') {
    return createRenderOnlyClient()
  }

  return getBrowserSession().syncSession(session)
}

// The gate as a callable, for REST callers (the MCP consent flow).
export const getFreshAccessToken = (): Promise<Token | undefined> => {
  if (typeof window === 'undefined') return Promise.resolve(undefined)
  return getBrowserSession().getFreshAccessToken()
}

export const forceMintToken = (): Promise<MintResult> => {
  if (typeof window === 'undefined') {
    return Promise.resolve('unavailable' as MintResult)
  }
  return getBrowserSession().forceMintToken()
}
